import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ChatFlow,
  CustomVariable,
  FlowDesign
} from '../../types/chat-flow.interface';
import { ScrollService } from '../../scroll.service';
import { BotStylingService } from 'src/app/shared/services/bot-styling.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { Subject, takeUntil } from 'rxjs';
import { ChatBotFlowSaService } from './chat-bot-flow-sa.service';
import { IconRegistryService } from 'src/app/shared/services/icon-registry.service';
import { TranslateService } from '@ngx-translate/core';
import { Step } from '../../types/steps.type';
import { CustomVariablesService } from './custom-variables.service';
import { Router } from '@angular/router';
import { replaceParamVariables } from 'src/app/shared/utils/replace-variables.util';
import { FocusManagementService } from 'src/app/shared/services/focus-management.service';
import { PosthogService } from 'src/app/shared/services/posthog.service';
import {
  FUNNEL_TITLE_FALLBACK,
  FUNNEL_DESCRIPTION_FALLBACK
} from 'src/app/shared/constants/global.constants';

@Component({
  selector: 'ui-chat-bot-sa',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './ui-chat-bot-sa.component.html',
  styleUrls: ['./ui-chat-bot-sa.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  host: {
    role: 'region',
    '[attr.aria-label]': 'funnelTitle',
    '[attr.aria-roledescription]': '"chatbot"',
    '[attr.aria-description]': 'funnelDescription || null'
  }
})
export class UiChatBotSaComponent implements OnInit, OnChanges, OnDestroy {
  private destroy$ = new Subject<void>();
  @Input() chatFlow: ChatFlow | undefined;
  @Input() flowDesign: FlowDesign | undefined;
  @Input() testMode: boolean | undefined;
  @Input() funnelLanguage: string | undefined;
  @Input() showLanguageSwitcher: boolean = true;
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  steps: Step[] | null = null;
  private shadowRoot!: ShadowRoot;
  dynamicVariables: CustomVariable | null = null;
  private languageInitialized = false;
  funnelTitle: string = FUNNEL_TITLE_FALLBACK['en'];
  funnelDescription: string = FUNNEL_DESCRIPTION_FALLBACK['en'];
  private onPageHide = (event: PageTransitionEvent) =>
    this.chatBotFlowSaService.notifyPageHidden({
      persisted: event.persisted
    });
  private onPageShow = () => this.chatBotFlowSaService.notifyPageRestored();
  private onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.chatBotFlowSaService.notifyPageRestored();
    }
  };

  get companyLogoAlt(): string {
    if (this.flowDesign?.company_name) {
      return `${this.flowDesign.company_name} logo`;
    }
    return this.translateService.instant('company_logo_alt');
  }

  get companyLogoAria(): string {
    if (this.flowDesign?.company_name) {
      const opensInNewTab = this.translateService.instant('OPENS_IN_NEW_TAB');
      return `${this.flowDesign.company_name} logo (${opensInNewTab})`;
    }
    return this.translateService.instant('company_logo_aria');
  }

  constructor(
    private scrollService: ScrollService,
    private stylingService: BotStylingService,
    private chatBotFlowSaService: ChatBotFlowSaService,
    private iconRegistry: IconRegistryService,
    private translateService: TranslateService,
    private elRef: ElementRef,
    private customVariablesService: CustomVariablesService,
    private router: Router,
    private focusManagementService: FocusManagementService,
    private titleService: Title,
    private posthogService: PosthogService
  ) {
    this.iconRegistry.initialize();
  }

  ngOnInit(): void {
    window.addEventListener('pagehide', this.onPageHide);
    // Returning from a share sheet or the back/forward cache means the user is
    // still with us, so re-enable genuine close reporting.
    window.addEventListener('pageshow', this.onPageShow);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.focusManagementService.blockInitialEntryFocus();

    // Apply initial styles
    this.shadowRoot = this.elRef.nativeElement.shadowRoot;
    const linkElement = document.createElement('link');
    linkElement.rel = 'stylesheet';
    linkElement.href =
      'https://fonts.googleapis.com/icon?family=Material+Icons';
    this.shadowRoot.appendChild(linkElement);

    this.customVariablesService.customVariables$
      .pipe(takeUntil(this.destroy$))
      .subscribe((variables) => {
        this.dynamicVariables = variables;
      });

    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        const container =
          this.shadowRoot.querySelector('.chat-bot-container') ||
          this.shadowRoot.querySelector('div');
        if (container) {
          container.setAttribute('data-lang', event.lang);
        }
        this.funnelLanguage = event.lang;
        this.updateAllMessages(event.lang);
        this.updateAccessibilityLabels();
      });
  }

  restoredAnswerAt(index: number): { [lang: string]: string } | null {
    return this.chatBotFlowSaService.getRestoredAnswer(index);
  }

  onChatbotUserInteraction(event: Event): void {
    if (!this.focusManagementService.isInitialEntryFocusBlocked()) return;
    if (!event.isTrusted) return;

    this.focusManagementService.releaseInitialEntryFocusBlock();
  }

  updateLanguageAttribute(lang: string): void {
    // Find a root container inside your shadow DOM
    const container =
      this.shadowRoot.querySelector('.chat-bot-container') ||
      this.shadowRoot.querySelector('div');

    if (container) {
      // Remove previous language attributes
      const allLangs = ['en', 'fr', 'de', 'es', 'it']; // Add all languages you support
      allLangs.forEach((language) => {
        container.removeAttribute(`data-lang-${language}`);
      });

      // Add new language attribute and class
      container.setAttribute(`data-lang`, lang);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.flowDesign) {
      this.stylingService.applyDynamicStyling(this.flowDesign, this.shadowRoot);
      // Apply custom CSS if available
      if (this.flowDesign.custom_css) {
        let customStyleElement = this.shadowRoot.querySelector(
          'style[data-custom-css]'
        );
        if (!customStyleElement) {
          customStyleElement = document.createElement('style');
          customStyleElement.setAttribute('data-custom-css', 'true');
          this.shadowRoot.appendChild(customStyleElement);
        }
        customStyleElement.textContent = this.flowDesign.custom_css;
      }
    }
    if (changes['flowDesign'] && this.flowDesign) {
      this.stylingService.applyDynamicStyling(this.flowDesign, this.shadowRoot);
      this.updateAccessibilityLabels();
    }

    if (changes['chatFlow'] && this.chatFlow && this.flowDesign) {
      this.chatBotFlowSaService.setFlowConfig(this.chatFlow);
      this.posthogService.register({
        tenant_id: this.chatFlow.tenant_slug,
        funnel_id: this.chatFlow.funnel_id,
        feedback_id: this.chatFlow.feedback_id
      });
      this.chatBotFlowSaService.userSteps$
        .pipe(takeUntil(this.destroy$))
        .subscribe((steps) => {
          this.steps = steps;
        });

      this.scrollService.triggerScrollToBottom$
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          setTimeout(() => {
            this.scrollToBottom();
          }, 100);
        });
    }
  }

  updateAllMessages(newLang: string): void {
    if (this.steps) {
      this.steps.forEach((step) => {
        step.messages.forEach((message) => {
          if (message.message[newLang]) {
            message.message[newLang] = this.updateMessageContent(
              message.message[newLang]
            );
          }
        });
      });
    }
  }

  updateAccessibilityLabels(): void {
    if (!this.flowDesign) return;
    const lang = this.funnelLanguage || 'en';

    // H1 label: funnel_title > fallback with company name > default
    const titleMsg = this.flowDesign.funnel_title?.message?.[lang];
    if (titleMsg) {
      this.funnelTitle = titleMsg;
    } else {
      const fallbackTitle =
        FUNNEL_TITLE_FALLBACK[lang] || FUNNEL_TITLE_FALLBACK['en'];
      const companyName = this.flowDesign.company_name || 'hypt';
      this.funnelTitle = fallbackTitle.replace('[Company Name]', companyName);
    }

    // Screen-reader description: funnel_description > fallback
    const descMsg = this.flowDesign.funnel_description?.message?.[lang];
    this.funnelDescription =
      descMsg ||
      FUNNEL_DESCRIPTION_FALLBACK[lang] ||
      FUNNEL_DESCRIPTION_FALLBACK['en'];

    // Dynamic page title
    const pageTitle = this.flowDesign.company_name
      ? `Feedback Chatbot - ${this.flowDesign.company_name}`
      : 'Feedback Chatbot';
    this.titleService.setTitle(pageTitle);
  }

  updateMessageContent(message: string): string {
    return replaceParamVariables(message, this.dynamicVariables);
  }

  prefetchAvatarImage(url: string): void {
    const img = new Image();
    img.src = url;
    img.onload = () => {
      if (this.flowDesign) {
        this.flowDesign.avatars.selected = url;
      }
    };
  }
  skipToContent(): void {
    const shadowRoot = this.elRef.nativeElement.shadowRoot as ShadowRoot | null;
    const chatContainer = shadowRoot?.querySelector('.app-container-chatbot');
    const target = (chatContainer || this.scrollContainer?.nativeElement) as
      | (HTMLElement & ParentNode)
      | null;

    if (!target) {
      return;
    }

    const focusable =
      this.focusManagementService.focusFirstFocusableDeep(target);
    if (focusable) {
      return;
    }

    target.setAttribute('tabindex', '-1');
    target.focus();
  }

  scrollToBottom() {
    const container = this.scrollContainer.nativeElement;
    container.style.scrollBehavior = 'smooth';
    container.scrollTop = container.scrollHeight;
  }

  onLanguageChanged(languageToSetTo: string): void {
    // First emission comes from language-switcher init, not a real user action
    const isUserSelection = this.languageInitialized;
    this.languageInitialized = true;

    // Get the full URL, drop the query, and split it into segments
    const urlSegments = this.router.url
      .split(/[?#]/)[0]
      .split('/')
      .filter((segment) => segment); // Remove empty segments
    const subdomain = urlSegments[0]; // First segment
    const funnelSelector = urlSegments.length > 2 ? urlSegments[1] : null; // Second segment if it exists
    const urlLanguage = subdomain ? urlSegments[urlSegments.length - 1] : null; // Last segment, null when embedded

    // The switcher resolves its own initial language (stored > browser > link),
    // so it can land on one the URL never asked for. Skip only when the bot and
    // the URL - and so the feedback record - already agree.
    const languageAlreadyInUse = languageToSetTo === this.funnelLanguage;
    const urlAlreadyInStep = !urlLanguage || languageToSetTo === urlLanguage;
    if (languageAlreadyInUse && urlAlreadyInStep) {
      return;
    }

    // Before the URL work, so an embedded bot with no route still switches
    const previousLanguage = this.funnelLanguage;
    this.funnelLanguage = languageToSetTo;
    this.translateService.use(languageToSetTo);

    // Ensure path segments are defined
    if (!subdomain) {
      console.error('Missing required path segments. Navigation aborted.');
      return;
    }

    if (isUserSelection) {
      this.posthogService.capture('chatbot_language_selected', {
        previous_language: previousLanguage,
        new_language: languageToSetTo
      });
    }

    // Build the new URL segments
    const newUrlSegments = funnelSelector
      ? [subdomain, funnelSelector, languageToSetTo]
      : [subdomain, languageToSetTo];

    // Update the `lang` path parameter in the URL
    this.router.navigate(newUrlSegments, {
      queryParamsHandling: 'preserve', // Preserve existing query parameters
      replaceUrl: !isUserSelection // Back must not return to the stale language
    });

    if (this.chatFlow && !this.testMode) {
      this.chatFlow.language.api.data.language = languageToSetTo;
      this.chatBotFlowSaService
        .updateFeedbackApi(this.chatFlow.language.api)
        .subscribe({
          error: (error) =>
            console.error('Language change API call failed', error)
        });
    }
  }
  ngAfterViewInit(): void {
    // Force initial styling application
    if (this.flowDesign) {
      setTimeout(() => {
        this.stylingService.applyDynamicStyling(
          this.flowDesign!,
          this.shadowRoot
        );
      }, 0);
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('pagehide', this.onPageHide);
    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.chatBotFlowSaService.notifyChatbotClosed();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
